// Horizontal product rail: prev/next buttons scroll by one visible page and disable at the ends.
// Optional [data-rail-dots]: renders one dot per page (active dot = current page); clicking a dot jumps to that page.
class SProductRail extends HTMLElement {
  #ac;
  #track;
  #dots;
  #pages = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const track = this.querySelector('[data-rail-track]');
    if (!track) return;
    this.#track = track;
    this.#dots = this.querySelector('[data-rail-dots]');

    this.querySelector('[data-rail-prev]')?.addEventListener('click', () => this.#scroll(-1), opts);
    this.querySelector('[data-rail-next]')?.addEventListener('click', () => this.#scroll(1), opts);
    this.#dots?.addEventListener('click', (event) => {
      const dot = event.target.closest('[data-page]');
      if (dot) this.#goTo(Number(dot.dataset.page));
    }, opts);
    track.addEventListener('scroll', () => this.#update(), { ...opts, passive: true });
    window.addEventListener('resize', () => this.#update(), opts);
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  get #rtl() {
    return getComputedStyle(this.#track).direction === 'rtl' ? -1 : 1;
  }

  #scroll(direction) {
    const track = this.#track;
    track.scrollBy({ left: direction * this.#rtl * track.clientWidth * 0.9, behavior: this.#behavior });
  }

  #goTo(page) {
    const track = this.#track;
    const max = track.scrollWidth - track.clientWidth;
    const left = Math.min(page * track.clientWidth, max);
    track.scrollTo({ left: left * this.#rtl, behavior: this.#behavior });
  }

  #renderDots(pages) {
    if (!this.#dots || pages === this.#pages) return;
    this.#pages = pages;
    const label = this.#dots.dataset.dotLabel || '';
    this.#dots.replaceChildren();
    if (pages < 2) return;
    for (let i = 0; i < pages; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.dataset.page = String(i);
      dot.setAttribute('aria-label', `${label} ${i + 1}`.trim());
      dot.className = 'h-[3px] w-5 cursor-pointer rounded-full bg-white/20 motion-safe:transition-[background-color,width] aria-[current=true]:w-6 aria-[current=true]:bg-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent';
      this.#dots.append(dot);
    }
  }

  #update() {
    const track = this.#track;
    const max = track.scrollWidth - track.clientWidth;
    const pos = Math.abs(track.scrollLeft);
    const prev = this.querySelector('[data-rail-prev]');
    const next = this.querySelector('[data-rail-next]');
    if (prev) prev.disabled = pos <= 2;
    if (next) next.disabled = pos >= max - 2;
    this.toggleAttribute('data-scrollable', max > 2);

    if (!this.#dots || track.clientWidth === 0) return;
    const pages = max > 2 ? Math.ceil(track.scrollWidth / track.clientWidth - 0.05) : 1;
    this.#renderDots(pages);
    const current = pos >= max - 2 ? pages - 1 : Math.round(pos / track.clientWidth);
    this.#dots.querySelectorAll('[data-page]').forEach((dot) => {
      dot.setAttribute('aria-current', String(Number(dot.dataset.page) === current));
    });
  }
}

if (!customElements.get('s-product-rail')) customElements.define('s-product-rail', SProductRail);
